/* eslint-disable max-lines */
import React from 'react';

import { PAYER_TYPES } from 'lib/constants/payer/payerTypes';
import numOrDefault from 'lib/helpers/numOrDefault';
import {
  Capitalize,
  formatClaimStatus,
  formatDate,
  formatDateNative,
  mString
} from 'lib/helpers/utility';

import {
  APPOINTMENT_RESOURCE_OPTIONS,
  CLAIM_NUMBER_OPTIONS,
  CLAIM_OPTIONS,
  CPT_CODE_OPTIONS,
  ICD_CODE_OPTIONS,
  PATIENT_OPTIONS,
  PAYER_OPTIONS,
  PROVIDER_CLAIM_OPTIONS,
  TAG_CLAIM_OPTIONS
} from 'components/shared/Filters/lib/filterOptions';
import TagsCell from 'components/shared/Tags/components/Cell/TagsCell';

import NavigateToERACell from '../PatientLedger/components/NavigateToERACell';

import ClaimActions from './components/ClaimActions';
import ClaimsTippy from './components/ClaimsTippy';
import ServiceCellTooltip from './components/ServiceCellTooltip';
import { calculatePatientResponsibility, getProcedureCodes, getProcedureSum } from './lib/helpers';

export const CLAIMS_SWITCH_ITEMS = [
  {
    label: 'Ready',
    value: 'ready'
  },
  {
    label: 'Acknowledged',
    value: 'accepted'
  },
  {
    label: 'Denied',
    value: 'denied'
  },
  {
    label: 'Paid',
    value: 'paid'
  },
  {
    label: 'Rejected',
    value: 'rejected'
  },
  {
    label: 'Pending',
    value: 'pending'
  },
  {
    label: 'Archived',
    value: 'archived'
  }
];

export const getColDefs = (name, timezone) => [
  {
    headerName: 'Claim ID',
    field: 'id',
    headerCheckboxSelection: true,
    checkboxSelection: (params) => !params.node.group
  },
  { headerName: 'Patient', field: 'patient_name', hide: name === 'patient_claims' },
  { headerName: 'Claim Number', field: 'claim_md_id' },
  { headerName: 'Ins Member ID', field: 'ins_number' },
  { headerName: 'Provider', field: 'provider_name' },
  { headerName: 'Payer', field: 'payer_name' },
  {
    headerName: 'Payer Type',
    valueFormatter: ({ value }) => (value ? Capitalize(value) : ''),
    field: 'kind'
  },
  {
    headerName: 'Status',
    field: 'state',
    valueFormatter: ({ value }) => formatClaimStatus({ status: value, toUpper: true })
  },
  {
    headerName: 'Type',
    valueFormatter: ({ value }) => (value ? Capitalize(value) : 'Primary'),
    field: 'payer_order'
  },
  {
    headerName: 'DOS',
    field: 'appointment_starts_at',
    valueGetter: (params) => {
      const date = params?.data?.appointment_starts_at;
      if (!date) return '-';

      return formatDate(date, timezone, true);
    }
  },
  {
    headerName: 'Services',
    field: 'procedures',
    cellRenderer: ServiceCellTooltip,
    sortable: false,
    valueGetter: (params) => getProcedureCodes(params.data?.invoice_procedures)
  },
  {
    headerName: 'Claim Submission Date',
    valueFormatter: ({ value }) => (value ? formatDate(value, timezone) : '-'),
    field: 'submitted_at'
  },
  {
    headerName: 'Charge Amount',
    field: 'charge_amount',
    aggFunc: 'sum',
    valueGetter: (params) => getProcedureSum(params.data?.invoice_procedures, 'charge'),
    valueFormatter: (params) => {
      const value = params.node.group
        ? params.value
        : getProcedureSum(params.data?.invoice_procedures, 'charge');
      return mString(numOrDefault(value));
    }
  },
  {
    headerName: 'Allowable Amount',
    field: 'allowable_amount',
    aggFunc: 'sum',
    valueGetter: (params) => getProcedureSum(params.data?.invoice_procedures, 'ins_payment'),
    valueFormatter: (params) => {
      const value = params.node.group
        ? params.value
        : getProcedureSum(params.data?.invoice_procedures, 'ins_payment');
      return mString(numOrDefault(value));
    }
  },
  {
    headerName: 'Insurance Paid Amount',
    field: 'insurance_paid_amount',
    aggFunc: 'sum',
    valueGetter: (params) => Number(params?.data?.insurance_payment?.total || 0),
    valueFormatter: (params) => {
      const value = params.node.group
        ? params.value
        : Number(params?.data?.insurance_payment?.total || 0);
      return mString(numOrDefault(value));
    }
  },
  {
    headerName: 'Patient Paid Amount',
    valueFormatter: ({ value }) => mString(numOrDefault(value)),
    aggFunc: 'sum',
    field: 'invoice_amount_paid'
  },
  {
    headerName: 'Patient Resp',
    valueFormatter: (params) =>
      params?.data?.state?.toLowerCase() === 'paid'
        ? mString(
            calculatePatientResponsibility(
              params?.data?.invoice_procedures,
              params?.data?.payer_order
            )
          )
        : mString(0),
    field: 'patient_responsibility',
    hide: true
  },
  {
    headerName: 'Insurance Payment Date',
    field: 'insurance_payment_date',
    valueGetter: (params) => {
      const dates = params?.data?.insurance_payment_date || [];

      return dates.map((date) => formatDateNative(date));
    },
    cellRenderer: ({ value }) => <ClaimsTippy value={value} />
  },
  {
    headerName: 'Patient Payment Date',
    field: 'patient_payment_date',
    valueGetter: (params) => {
      const dates = params?.data?.patient_payment_date || [];

      return dates.map((date) => formatDate(date, timezone));
    },
    cellRenderer: ({ value }) => <ClaimsTippy value={value} />
  },
  {
    field: 'ERA/EOB',
    sortable: false,
    headerName: 'ERA/EOB',
    cellRenderer: NavigateToERACell
  },
  {
    field: 'tagLinks',
    headerName: 'Tags',
    cellClass: 'no-border',
    minWidth: 300,
    cellRenderer: ({ value, data }) => (
      <TagsCell
        value={value}
        kind="claim"
        data={data}
        query={{ keys: ['claims'], object: 'claims' }}
        refTable="claim"
      />
    ),
    pinned: 'right',
    sortable: false
  },
  { field: 'resource_name', headerName: 'Resource' },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: ClaimActions,
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];

export const DEFAULT_COLUMN_DEFS = { resizable: true, enableRowGroup: true };

export const GRID_OPTIONS = {
  rowGroupPanelShow: 'always',
  animateRows: true,
  suppressAggFuncInHeader: true,
  enableRangeSelection: true
};

export const DEFAULT_FILTERS = {
  date: {
    type: 'date-range',
    title: 'Date of Service',
    icon: 'calendar-mono'
  },
  submission_date: {
    type: 'date-range',
    title: 'Date of Submission',
    icon: 'calendar-1-mono'
  },
  paid_date: {
    type: 'date-range',
    title: 'Date of Payment',
    icon: 'calendar-2-mono'
  },
  id: CLAIM_OPTIONS,
  patient: PATIENT_OPTIONS,
  payer: PAYER_OPTIONS,
  provider: PROVIDER_CLAIM_OPTIONS,
  appointment_resource: APPOINTMENT_RESOURCE_OPTIONS,
  claimNumber: CLAIM_NUMBER_OPTIONS,
  cpt_code: { ...CPT_CODE_OPTIONS, allowed_operators: ['is', 'is not', 'is any of'] },
  icd_code: { ...ICD_CODE_OPTIONS, allowed_operators: ['is', 'is not', 'is any of'] },
  insMemberId: {
    title: 'Ins. Member ID',
    placeholder: 'Search Ins. Member ID',
    queryKey: 'claim_ins_numbers.options',
    icon: 'user-octagon-mono'
  },
  tags: TAG_CLAIM_OPTIONS,
  payer_type: {
    title: 'Payer Type',
    placeholder: 'Search by payer type',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: PAYER_TYPES.filter((payer_type) =>
          payer_type.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'claim_payer_types.options',
    icon: 'payers-linear'
  },
  status: {
    title: 'Status',
    placeholder: 'Search by status',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: CLAIMS_SWITCH_ITEMS.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'claim_statuses.options',
    icon: 'status-mono'
  }
};

export const getPatientDefaultFilters = () => {
  const newDefaultFilters = { ...DEFAULT_FILTERS };
  delete newDefaultFilters.patient;

  return newDefaultFilters;
};
